import React, { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import humanizeDuration from 'humanize-duration'
import { ClientOnShow } from '../../clients/Show'
import HistoryBack from '../../common/HistoryBack'
import Player from './Player'
import Describe from './Describe'
import withWebsocket, { WithWebsocketProps } from '~/decorators/withWebsocket'
import MainContent from '~/components/MainContent'
import ClientHeader from '~/components/common/ClientHeader'
import { formatDate } from '~/utils/dateFns'
import { CallOnShow, CallOnShowRaw } from '../types'
import TelemarketingCustomData from './TelemarketingCustomData'
import { convertCall } from '~/service/call/callConverter'

type ApiDataType = {
  call: CallOnShowRaw
  client: ClientOnShow
  recording: string | false
}

const Show: React.FC<WithWebsocketProps<ApiDataType>> = ({
  apiData: apiDataInit,
  channel,
}) => {
  const [{ call, client, recording }, setApiData] = useState(() => {
    return { ...apiDataInit, call: convertCall(apiDataInit.call) }
  })

  useEffect(() => {
    channel.on('recording', ({ recording, duration }) => {
      setApiData(data => ({
        ...data,
        recording,
        call: {
          ...data.call,
          duration,
        },
      }))
    })

    channel.on('update', ({ call }) => {
      setApiData(data => ({
        ...data,
        call,
      }))
    })
  }, [])

  return (
    <MainContent className="is-whiteBg call-show">
      <HistoryBack />

      <ClientHeader call={call} client={client} />

      <div className="call-show-details">
        {call.voicebot ? (
          <div className="call-show-details-icon">
            <span className="fal fa-robot" />
            VoiceBot
          </div>
        ) : (
          <div
            className={classNames(
              'call-show-details-icon call-show-details-status',
              {
                'is-missed': !call.answered,
                'is-answered': call.answered,
                'is-outbound': call.outbound,
                'is-inbound': !call.outbound,
              }
            )}
          >
            <span className="far fa-arrow-up" />
            {call.outbound
              ? intl.formatMessage({ id: 'call / details / status / outbound' })
              : intl.formatMessage({
                  id: 'call / details / status / inbound',
                })}{' '}
            {!call.answered
              ? intl.formatMessage({ id: 'call / details / status / missed' })
              : intl.formatMessage({
                  id: 'call / details / status / answered',
                })}
          </div>
        )}

        {call.ivr && (
          <div className="call-show-details-icon">
            <span className="far fa-info" />
            {call.ivr}
          </div>
        )}

        <div className="call-show-details-icon">
          <span className="far fa-calendar-alt" />
          {formatDate(call.createdAt, 'PPp')}
        </div>

        {call.duration && (
          <div className="call-show-details-icon">
            <span className="far fa-stopwatch" />
            {humanizeDuration(call.duration, {
              round: true,
              language: intl.locale,
            })}
          </div>
        )}

        {recording && (
          <div className="call-show-details-player">
            <Player recording={recording} />
          </div>
        )}
      </div>

      {call.answered && <Describe call={call} />}

      {call.tmContact?.customData && <TelemarketingCustomData call={call} />}
    </MainContent>
  )
}
const ShowWithWebsocket = withWebsocket(
  Show,
  params => 'phone_call:' + params.id
)

export default ShowWithWebsocket
